import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import MuPb from "../../widgets/MuPb";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Container,
} from "@mui/material";
import { getErrorResponse, toastError } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";

const generateProfileBanner = (username) => {
  const colors = ["#4CAF50", "#2196F3", "#F44336", "#FF9800", "#9C27B0"];
  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  return `https://via.placeholder.com/800x200/${randomColor.replace(
    "#",
    ""
  )}?text=${encodeURIComponent(username)}`;
};

const VisitorDetail = () => {
  const { id } = useParams();
  const [resending, setResending] = useState(false);
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-visitor?id=${id}`
  );

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const {
    _id,
    username,
    user_id,
    barcode_id,
    country,
    state,
    city,
    title,
    firstName,
    lastName,
    mobileNo,
    email,
    visitorGroup,
    companyName,
    industrySector,
    howDidYouFindOutAboutThisEvent,
    productCategories,
    qrLink,
    createdOn,
    updatedOn,
  } = data;

  // Generate a placeholder banner since profile_banner is not available
  const profileBanner = generateProfileBanner(username);

  return (
    <Box>
      <BackBtn />
      <Container maxWidth="md">
        <Card>
          {/* Profile Banner */}
          <Box
            component="img"
            src={profileBanner}
            alt="Profile Banner"
            sx={{ width: "100%", height: 200, objectFit: "cover" }}
          />
          <CardContent>
            <Grid container spacing={2}>
              {/* Name and Visitor Group */}
              <Grid item xs={12} sm={6}>
                <Typography variant="h5">
                  {title} {firstName} {lastName}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {visitorGroup}
                </Typography>
                <Typography variant="subtitle1">{companyName}</Typography>
              </Grid>
              {/* Contact Details */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  <strong>Email:</strong> {email}
                </Typography>
                <Typography variant="body1">
                  <strong>Mobile:</strong> {mobileNo}
                </Typography>
                <Typography variant="body1">
                  <strong>Address:</strong>
                  {data?.address ?? ""}, {city?.name ?? ""}, {state?.name ?? ""}
                </Typography>
                {country?.code && (
                  <img
                    src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                    alt={`${country.name} flag`}
                    style={{ width: "30px", marginTop: "5px" }}
                  />
                )}
              </Grid>
              {/* Additional Details */}
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Username</strong>
                        </TableCell>
                        <TableCell>{username}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>User ID</strong>
                        </TableCell>
                        <TableCell>{user_id}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Barcode ID</strong>
                        </TableCell>
                        <TableCell>{barcode_id}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Industry Sector</strong>
                        </TableCell>
                        <TableCell>{industrySector}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Event Discovery</strong>
                        </TableCell>
                        <TableCell>{howDidYouFindOutAboutThisEvent}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Created On</strong>
                        </TableCell>
                        <TableCell>
                          {new Date(createdOn).toLocaleString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Updated On</strong>
                        </TableCell>
                        <TableCell>
                          {new Date(updatedOn).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              {/* Product Categories */}
              {productCategories && productCategories.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6">Product Categories</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {productCategories.map((product, index) => (
                      <Chip
                        key={index}
                        label={`${product.MainCategory}: ${product.Category}`}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
              {/* QR Code */}
              {qrLink && (
                <Grid item xs={12}>
                  <Typography variant="h6">QR Code</Typography>
                  <img
                    src={qrLink}
                    alt="QR Code"
                    style={{ width: "150px", height: "150px" }}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
        <LoadingButton
          loading={resending}
          onClick={async () => {
            try {
              setResending(true);
              const { data } = await axios.post(
                `${API_ENDPOINT}/admin/resend-email-by-a`,
                { email: email },
                getHeader()
              );
              toast.success(data.message);
            } catch (error) {
              toastError(error);
            } finally {
              setResending(false);
            }
          }}
          variant="outlined"
          size="small"
        >
          Resend Badge Email
        </LoadingButton>
      </Container>
    </Box>
  );
};

export default VisitorDetail;
